<script>
import patientService from "@/services/patient";
import totpService from "@/services/totp";
import medicalInsuranceService from "@/services/medical-insurance";
import medicalServices from "@/services/medical-services";
import CardPatient from "./CardPatient.vue";
import CardResult from "./CardResult.vue";
import debounce from "debounce";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "Validation",
  components: {
    CardPatient,
    CardResult,
    BaseSubNav,
  },
  data() {
    return {
      query: {
        medicalInsuranceObj: {
          insurance: undefined,
          publicInsurance: {
            publicId: "",
            name: "",
          },
          number: "",
          plan: "",
        },
      },
      medicalService: "",
      medicalInsurances: [],
      medicalServices: [],
      patientFound: null,
      patientSelected: null,
      codeValidation: "",
      validationResult: null,

      searchingPatient: false,
      validatingCode: false,
      checkPatient: true,
      isLoadingMedicalInsurances: false,
      isLoadingMedicalServices: false,

      errorPatient: null,
      errorCode: null,
    };
  },

  async created() {
    await this.loadMedicalInsurances();
  },

  methods: {
    async loadMedicalInsurances() {
      this.isLoadingMedicalInsurances = true;

      try {
        const response = await medicalInsuranceService.get();
        this.medicalInsurances = response.docs || response || [];
      } catch (error) {
        console.error("Error al cargar medical insurance:", error);
        this.$message.error("Error al cargar las prepagas");
      } finally {
        this.isLoadingMedicalInsurances = false;
      }
    },

    getMedicalServices: debounce(async function (searchTerm) {
      this.isLoadingMedicalServices = true;

      try {
        const query = {};
        if (searchTerm) {
          query.name = { $regex: searchTerm, $options: "i" };
        }

        const response = await medicalServices.get(query);
        this.medicalServices = response.docs || response || [];
      } catch (error) {
        console.error("Error al buscar servicios médicos:", error);
        this.$message.error("Error al buscar las prestaciones");
        this.medicalServices = [];
      } finally {
        this.isLoadingMedicalServices = false;
      }
    }, DEBOUNCE_MS_TIMEOUT),

    async searchPatient() {
      if (
        !this.query.medicalInsuranceObj?.publicInsurance?.publicId &&
        !this.query.medicalInsuranceObj?.number &&
        !this.query.medicalInsuranceObj?.plan
      ) {
        this.errorPatient = "Al menos un campo es requerido para la búsqueda";
        return;
      }

      this.searchingPatient = true;
      this.errorPatient = null;
      this.patientFound = null;

      try {
        const query = {};

        const medicalInsuranceObj = this.query.medicalInsuranceObj || {};
        const publicInsurance = medicalInsuranceObj.publicInsurance || {};
        const publicId = publicInsurance?.publicId;
        const numberOrDni = medicalInsuranceObj?.number;
        const plan = medicalInsuranceObj?.plan;

        if (publicId) {
          const publicIdKey =
            "patient.medicalInsuranceObj.publicInsurance.publicId";
          query[publicIdKey] = publicId;
        }

        if (plan) {
          const planKey = "patient.medicalInsuranceObj.plan";
          query[planKey] = plan;
        }

        if (numberOrDni) {
          query.$or = [
            { "patient.medicalInsuranceObj.number": numberOrDni },
            { "governmentId.number": numberOrDni },
          ];
        }

        const response = await patientService.get(query);

        if (response.docs && response.docs.length > 0) {
          this.patientFound = response.docs;

          if (response.docs.length > 1) {
            this.$message.info(
              `Se encontraron ${response.docs.length} pacientes. Mostrando el primero.`
            );
          }
        } else {
          this.errorPatient =
            "No se encontró ningún paciente con los datos proporcionados";
        }
      } catch (error) {
        console.error("Error al buscar paciente:", error);
        this.errorPatient =
          "Error al buscar paciente: " + (error.message || "Error de conexión");
      } finally {
        this.searchingPatient = false;
      }
    },

    async validateCode() {
      if (!this.codeValidation.trim()) {
        this.errorCode = "Por favor ingresa un token";
        return;
      }

      if (!this.patientSelected || !this.patientSelected._id) {
        this.errorCode = "Por favor selecciona un paciente primero";
        return;
      }

      if (!this.medicalService) {
        this.errorCode = "Por favor selecciona una prestación";
        return;
      }

      this.validatingCode = true;
      this.errorCode = null;
      this.validationResult = null;

      try {
        const response = await totpService.verify(
          this.patientSelected._id,
          this.medicalService,
          this.codeValidation,
          this.checkPatient
        );

        this.validationResult = response;

        if (response.isTokenValid) {
          this.$message.success("Token válido");
        } else {
          this.$message.error("Token no válido, intente nuevamente");
        }
      } catch (error) {
        console.error("Error en la validación:", error);
        this.errorCode = "Error en la validación";
      } finally {
        this.validatingCode = false;
      }
    },

    clearPatientForm() {
      this.medicalService = "";
      this.query = {
        medicalInsuranceObj: {
          insurance: undefined,
          publicInsurance: {
            publicId: "",
            name: "",
          },
          number: "",
          plan: "",
        },
      };
      this.patientFound = null;
      this.patientSelected = null;
      this.errorPatient = null;
      this.codeValidation = "";
      this.validationResult = null;
      this.errorCode = null;
    },

    clearCodeForm() {
      this.codeValidation = "";
      this.validationResult = null;
      this.errorCode = null;
    },

    handlePatientSelection(patient) {
      this.patientSelected = patient;
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav(
      navigationType="validation"
      :showNavigation="true"
    )
      template(#title)
        | Validaciones
  div.validation-container
    div.validation-box
      el-card.validation-card
        div(slot="header")
          h3.validation-header Paciente

        el-form.form-container(:model="$data")
          div.container-first-box
            div.container-inputs
              el-form-item.form-item-full(label="Número de Afiliado/DNI")
                el-input.form-input-full(
                  v-model="query.medicalInsuranceObj.number"
                  placeholder="Ingrese número de afiliado o DNI"
                  clearable
                )
              
              el-form-item.form-item-full(label="Prepaga")
                el-select.form-input-full(
                  v-model="query.medicalInsuranceObj.publicInsurance.publicId"
                  placeholder="Seleccione la prepaga"
                  :loading="isLoadingMedicalInsurances"
                  clearable
                  filterable
                )
                  el-option(
                    v-for="insurance in medicalInsurances"
                    :key="insurance._id"
                    :label="insurance.name"
                    :value="insurance._id"
                  )
              
              el-form-item.form-item-full(label="Plan")
                el-input.form-input-full(
                  v-model="query.medicalInsuranceObj.plan"
                  placeholder="Ingrese el plan"
                  clearable
                )
              
              el-form-item.form-buttons-container
                el-button.form-button-primary(
                  type="primary"
                  @click="searchPatient"
                  :loading="searchingPatient"
                ) Buscar Paciente
                
                el-button.form-button-secondary(
                  @click="clearPatientForm"
                ) Limpiar

      el-card.validation-card
        div.patients-container(v-if="patientFound")
          CardPatient(
            v-for="(patient, index) in patientFound"
            :key="patient._id || index"
            :patient="patient"
            :is-selected="patientSelected ? patientSelected._id === patient._id : false"
            @patient-selected="handlePatientSelection"
          )
        
        div.error-container(v-if="errorPatient")
          p.error-message {{ errorPatient }}
          el-alert(
            :title="errorPatient"
            type="error"
            :closable="false"
          )

    div.validation-box-container
      div.validation-box-right
        el-card.validation-card
          div(slot="header")
            h3.validation-header Prestación

          el-form.form-container(:model="$data")
            el-form-item.form-item-full(label="Prestación")
              el-select.form-input-full(
                v-model="medicalService"
                placeholder="Ingrese una prestación"
                :loading="isLoadingMedicalServices"
                clearable
                filterable
                remote
                :remote-method="getMedicalServices"
              )
                el-option(
                  v-for="service in medicalServices"
                  :key="service._id"
                  :label="service.code + ' - ' + service.name"
                  :value="service._id"
                )

        div.token-container
          el-card.validation-card
            div(slot="header")
              h3.validation-header Token

            el-form.form-container(:model="$data")
              el-form-item(label="Token")
                el-input.token-input(
                  v-model="codeValidation"
                  placeholder="Ingrese el código de 3 dígitos"
                  clearable
                  maxlength="3"
                  :disabled="!patientSelected"
                )
          div.validation-actions-container
            div.validation-buttons-container
              el-button.form-button-primary(
                type="primary"
                @click="validateCode"
                :loading="validatingCode"
                :disabled="!codeValidation || !patientSelected || validatingCode"
              ) Validar
              
              el-button.form-button-secondary(
                @click="clearCodeForm"
                :disabled="!codeValidation || !patientSelected || validatingCode"
              ) Limpiar
          
      CardResult(
        :validation-result="validationResult"
        :error-code="errorCode"
      )
</template>

<style lang="scss" scoped>
.validation-container {
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.validation-box-container {
  display: flex;
  flex-direction: column;
  width: 50%;
  gap: 20px;
}

.validation-box-right {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 218px;
  width: 100%;
}

.validation-box {
  width: 50%;
}

.validation-card {
  .el-card__header {
    background-color: #f5f7fa;

    h3 {
      margin: 0;
      color: #303133;
    }
  }
}

.validation-header {
  margin: 0;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.container-first-box,
.validation-box {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
}

.container-inputs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-item-full,
.form-input-full,
.validation-actions-container {
  width: 100%;
}

.patients-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-buttons-container,
.validation-actions-container,
.validation-buttons-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 10px;
}

.form-button-primary,
.form-button-secondary {
  border-radius: 10px;
  margin: 0;
  width: 100%;
}

.error-container,
.patients-container {
  margin-top: 20px;
}

.error-message {
  color: red;
  margin: 0;
}

.container-form-buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
}

.el-form-item {
  margin-bottom: 18px;
}

.token-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
